import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { throttle } from 'throttle-debounce';
import { TypographyWithCopy } from 'console/shared/components/fields';
import { rusDateTime } from 'webui/utils/time';
import { usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { select_device_gps_fields } from './utils';
import { useAPI } from 'console/shared/api/api-context';
import branding from 'shared/app/model/branding';
import { ZONTGeocoding } from 'console/shared/api/geocoding';
import { getGPSSignal, GPSIcon } from './index';
import { useHelpPopup } from 'shared/hooks/ui/help-popup';
import { translate_device_icon_state } from 'console/device/icon/transform';
import { DeviceStateIndicator } from '../device-state';
import { selectDevice } from 'entities/device/lib';
const AdditionalGPSState = memo(function AdditionalGPSState(props) {
    const { icon_state, last_connect_time, last_receive_time } = props;
    return (_jsxs(_Fragment, { children: [icon_state && (_jsx(TypographyWithCopy, { label: "\u0421\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435", value: _jsxs(Box, { display: "flex", alignItems: "center", columnGap: 1, children: [_jsx(DeviceStateIndicator, { state: icon_state }), _jsx(Typography, { children: translate_device_icon_state[icon_state] })] }) })), (last_receive_time ?? last_connect_time) && (_jsx(TypographyWithCopy, { label: "\u041F\u043E\u0441\u043B\u0435\u0434\u043D\u0438\u0435 \u0434\u0430\u043D\u043D\u044B\u0435", value: rusDateTime(new Date((last_receive_time ?? last_connect_time ?? 0) * 1e3)) }))] }));
});
const GPSIconTooltip = memo(function GPSIconTooltip(props) {
    const { Tooltip, gps, last_gps, device_id } = props;
    const [last_address, setLastAddress] = useState([]);
    const api = useAPI();
    const throttledRequestAddress = useMemo(() => throttle(60e3, async (api, last, setLastAddress) => {
        ZONTGeocoding.geocode_many_and_extract_promise(api, [[last.x, last.y]], 'state-tab').then((results) => {
            setLastAddress(results);
        });
    }), [device_id]);
    useEffect(() => {
        if (last_gps != null) {
            throttledRequestAddress(api, last_gps[0], setLastAddress);
        }
    }, [last_gps, setLastAddress]);
    const device = usePartialAppSelector(selectDevice, device_id);
    const gps_device = select_device_gps_fields(device);
    const { last_connect_time, last_receive_time, iconState } = gps_device || {};
    const { satellites } = useMemo(() => {
        if (gps) {
            return gps;
        }
        return { hdop: null, state: null, satellites: null };
    }, [gps]);
    const speed = last_gps?.[0]?.speed;
    const { label } = useMemo(() => getGPSSignal({ gps, last_gps: last_gps?.[0] }), [gps, last_gps]);
    return (_jsx(Tooltip, { children: _jsxs(Box, { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gridTemplateRows: "repeat(auto-fill, minmax(50px, 1fr))", gap: 2, children: [branding.additional_state_in_gps_card && (_jsx(AdditionalGPSState, { icon_state: iconState, last_connect_time: last_connect_time, last_receive_time: last_receive_time })), _jsx(TypographyWithCopy, { label: "\u0423\u0440\u043E\u0432\u0435\u043D\u044C \u0441\u0438\u0433\u043D\u0430\u043B\u0430", value: label }), _jsx(TypographyWithCopy, { label: "\u0410\u0434\u0440\u0435\u0441", value: last_address }), _jsx(TypographyWithCopy, { label: "\u0421\u043A\u043E\u0440\u043E\u0441\u0442\u044C", value: `${speed ?? 0} км/ч` }), _jsx(TypographyWithCopy, { label: "\u0421\u043F\u0443\u0442\u043D\u0438\u043A\u0438", value: last_gps?.[0]?.satellites ?? satellites })] }) }));
});
export const GPSIconWithInfo = memo(function GPSIconWithInfo(props) {
    const { gps, last_gps, online, device_id, ...rest } = props;
    const [HelpPopup, open] = useHelpPopup();
    return (_jsxs(_Fragment, { children: [_jsx(GPSIcon, { online: online, gps: gps, open: open, last_gps: last_gps, ...rest }), _jsx(GPSIconTooltip, { Tooltip: HelpPopup, gps: gps, online: online, last_gps: last_gps, device_id: device_id })] }));
});
